import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/api/queries'
import { useUser } from '@/hooks/useUser'
import {
  CollabClient,
  isRealtimeCollabFeatureEnabled,
  type CollabConnectionState,
  type DocCursorPayload,
  type DocFieldChangedPayload,
  type DocFieldSaveAck,
  type DocJoinAck,
  type DocPresenceJoinedPayload,
  type DocPresenceLeftPayload,
  type PresenceParticipant,
} from '@/lib/realtime'

/**
 * Lightweight Socket.IO-backed collaboration for a single document.
 *
 * Surface intentionally narrow:
 *
 * - `presence` — every collaborator currently joined to the doc room
 *   (including the local user). Updated on `doc:presence:{joined,left}`.
 * - `connectionState` — `'idle' | 'connecting' | 'connected' |
 *   'reconnecting' | 'disconnected' | 'error'`. Drives the
 *   `<CollabPresence>` status pill so users see "Offline — your edits
 *   will save when you reconnect" instead of silently losing writes.
 * - `cursor(field_uuid)` — broadcasts a `doc:cursor` to siblings so the
 *   focused-field outline can decorate the field the *other* user has
 *   focused.
 * - `saveField(…)` — optional realtime save path that uses the OCC
 *   contract (`expected_updated_at` echoed back, server returns
 *   `current_value` + `current_updated_at` on conflict so the client
 *   rebases). The REST PATCH path remains authoritative; this method
 *   exists so future autosave-on-every-keystroke flows have a typed
 *   binding without touching this hook again.
 *
 * Disabled when the feature flag is off — every method becomes a no-op
 * ack and the hook never opens a socket, so the realtime layer doesn't
 * tax dev mode or environments that haven't enabled it yet.
 */
export type CollabHookState = {
  enabled: boolean
  connectionState: CollabConnectionState
  /** All participants in the room — sorted by display so the avatar pill
   *  ordering is stable across re-renders. */
  presence: PresenceParticipant[]
  /** session_id → field_uuid currently focused by that session, for the
   *  per-field outline decoration. */
  cursors: Record<string, string | null>
  /** The local user's own session id once joined, or null. */
  selfSessionId: string | null
  /** Last server-emitted error reason ("forbidden", "stale", ...). Cleared
   *  whenever a subsequent action succeeds. */
  lastError: string | null
  /** Broadcast the local cursor's focused field. */
  setCursor: (fieldUuid: string | null) => void
  /** Optional realtime save path. Returns the server ack so callers can
   *  branch on `ok` and rebase from `current_value` / `current_updated_at`
   *  on a `stale` conflict. */
  saveField: (input: {
    fieldUuid: string
    value: string | null
    expectedUpdatedAt: string | null
  }) => Promise<DocFieldSaveAck>
}

const IDLE_STATE: CollabHookState = {
  enabled: false,
  connectionState: 'idle',
  presence: [],
  cursors: {},
  selfSessionId: null,
  lastError: null,
  setCursor: () => {},
  saveField: async () => ({ ok: false, reason: 'feature_disabled' }),
}

interface UseDocumentCollabOptions {
  documentId: string | null | undefined
  /** Display name for the local user (used as the avatar-pill label).
   *  Falls back to email → user_id when null. */
  display?: string | null
  /** Override the auto-detected feature flag — used by tests to force the
   *  realtime path on or off without touching env vars. */
  forceEnabled?: boolean
}

export function useDocumentCollab({
  documentId,
  display,
  forceEnabled,
}: UseDocumentCollabOptions): CollabHookState {
  const enabled = forceEnabled ?? isRealtimeCollabFeatureEnabled()
  const { isAuthenticated, getAccessToken } = useUser()
  const queryClient = useQueryClient()

  const [connectionState, setConnectionState] = useState<CollabConnectionState>('idle')
  const [presence, setPresence] = useState<PresenceParticipant[]>([])
  const [cursors, setCursors] = useState<Record<string, string | null>>({})
  const [selfSessionId, setSelfSessionId] = useState<string | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)

  // Stash live deps on a ref so the connect-effect doesn't tear-down on
  // every render. The socket is connection-stable; the deps it observes
  // (token getter, display) are pulled out of the ref at use time.
  const tokenRef = useRef(getAccessToken)
  const displayRef = useRef(display)
  const clientRef = useRef<CollabClient | null>(null)
  useEffect(() => {
    tokenRef.current = getAccessToken
    displayRef.current = display
  })

  // Reset the per-doc UI state when the document under view changes so a
  // stale presence list from the previous doc doesn't briefly render.
  // The setState calls run synchronously inside the effect because the
  // reset has to land BEFORE the connect effect attempts to repopulate
  // presence for the new doc — a debounced/queued reset would race with
  // the next room's `doc:join` ack and produce flicker.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPresence([])
    setCursors({})
    setLastError(null)
  }, [documentId])

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConnectionState('idle')
      return
    }
    if (!isAuthenticated || !documentId) {
      // Wait for auth + a doc to bind to. The hook intentionally does
      // NOT speculatively open a socket while the user is signed out.
      setConnectionState('idle')
      return
    }

    setConnectionState('connecting')
    const client = new CollabClient({
      getAccessToken: () => tokenRef.current(),
    })
    clientRef.current = client
    const socket = client.connect()
    let cancelled = false

    const joinRoom = async () => {
      try {
        const ack = await socket
          .timeout(10_000)
          .emitWithAck('doc:join', {
            doc_uuid: documentId,
            display: displayRef.current ?? null,
          })
        if (cancelled) return
        const join = ack as DocJoinAck
        if (!join.ok) {
          setConnectionState('error')
          setLastError(join.reason ?? 'join_failed')
          return
        }
        setLastError(null)
        setSelfSessionId(join.self?.session_id ?? null)
        setPresence(join.presence ?? [])
      } catch (err) {
        if (cancelled) return
        setConnectionState('error')
        setLastError(err instanceof Error ? err.message : 'join_failed')
      }
    }

    socket.on('connect', () => {
      setConnectionState('connected')
      void joinRoom()
    })
    socket.on('connect_error', (err: Error) => {
      setConnectionState('error')
      setLastError(err.message || 'connect_error')
    })
    socket.io.on('reconnect_attempt', () => {
      setConnectionState('reconnecting')
    })
    socket.on('disconnect', (reason: string) => {
      setConnectionState(reason === 'io client disconnect' ? 'disconnected' : 'reconnecting')
    })

    socket.on('doc:presence:joined', (payload: DocPresenceJoinedPayload) => {
      if (payload.doc_uuid !== documentId) return
      setPresence(payload.presence)
    })
    socket.on('doc:presence:left', (payload: DocPresenceLeftPayload) => {
      if (payload.doc_uuid !== documentId) return
      setPresence(payload.presence)
      setCursors((prev) => {
        const next = { ...prev }
        delete next[payload.session_id]
        return next
      })
    })
    socket.on('doc:cursor', (payload: DocCursorPayload) => {
      if (payload.doc_uuid !== documentId) return
      setCursors((prev) => ({ ...prev, [payload.session_id]: payload.field_uuid }))
    })
    socket.on('doc:field_changed', (payload: DocFieldChangedPayload) => {
      if (payload.doc_uuid !== documentId) return
      // The canonical value is in the payload; invalidate the document
      // query so the FieldsPanel picks the new value out of the next
      // cache read. We deliberately don't write into the cache directly
      // — there are multiple cached shapes (document detail, audit log,
      // sharing list) that all need refreshing on a field change.
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.detail(documentId),
        refetchType: 'active',
      })
      // The audit-log query key includes an `actor` filter; invalidating
      // the bare prefix matches every active variant by TanStack's
      // default partial-match semantics.
      queryClient.invalidateQueries({
        queryKey: ['documents', 'audit', documentId],
        refetchType: 'active',
      })
    })
    socket.on('doc:comments_changed', (payload: { doc_uuid: string }) => {
      if (payload.doc_uuid !== documentId) return
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.comments(documentId),
        refetchType: 'all',
      })
    })

    return () => {
      cancelled = true
      // Best-effort leave so the server broadcasts a `doc:presence:left`
      // immediately rather than waiting for the grace-window eviction.
      socket
        .timeout(2_000)
        .emit('doc:leave', { doc_uuid: documentId })
      client.dispose()
      clientRef.current = null
      setSelfSessionId(null)
      setConnectionState('disconnected')
    }
  }, [enabled, isAuthenticated, documentId, queryClient])

  const setCursor = useCallback(
    (fieldUuid: string | null) => {
      const client = clientRef.current
      if (!enabled || !client || !documentId) return
      const socket = client.connect()
      if (!socket.connected) return
      socket.emit('doc:cursor', {
        doc_uuid: documentId,
        field_uuid: fieldUuid,
      })
    },
    [enabled, documentId],
  )

  const saveField = useCallback(
    async (input: {
      fieldUuid: string
      value: string | null
      expectedUpdatedAt: string | null
    }): Promise<DocFieldSaveAck> => {
      if (!enabled || !documentId) {
        return { ok: false, reason: 'feature_disabled' }
      }
      const client = clientRef.current
      if (!client) {
        return { ok: false, reason: 'not_connected' }
      }
      const socket = client.connect()
      if (!socket.connected) {
        return { ok: false, reason: 'not_connected' }
      }
      try {
        const ack = (await socket.timeout(8_000).emitWithAck('doc:field_save', {
          doc_uuid: documentId,
          field_uuid: input.fieldUuid,
          value: input.value,
          expected_updated_at: input.expectedUpdatedAt,
        })) as DocFieldSaveAck
        if (!ack.ok && ack.reason) setLastError(ack.reason)
        else setLastError(null)
        return ack
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'save_failed'
        setLastError(reason)
        return { ok: false, reason }
      }
    },
    [enabled, documentId],
  )

  if (!enabled) return IDLE_STATE

  return {
    enabled,
    connectionState,
    presence,
    cursors,
    selfSessionId,
    lastError,
    setCursor,
    saveField,
  }
}
